import { AgGridReact } from 'ag-grid-react';
import { columnDefs, defaultColDef } from './utils/css-defination-config.jsx';
import { getClassesForSpecificGroup } from '../../services/apiService.js';
import { useState, useRef, useEffect } from 'react';
import 'ag-grid-enterprise';

function ManageClassroomCssTable({ allGroups, activeTab }) {
  const [rowData, setRowData] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSize] = useState(10); // fixed for now, can make dynamic later

  const gridApiRef = useRef(null);
  const isFetchingRef = useRef(false);
  const columnDefsInitializedRef = useRef(false);

  // ✅ Filter only classes that have ended
  const comparator = (data) => {
    // return data?.filter((item) => new Date(item.endTime) < new Date());
    return data;
  };

  // ✅ Fetch paginated data (from API)
  const fetchData = async (groupName, page) => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;

    try {
      const res = await getClassesForSpecificGroup(groupName, page);
      const { data, totalPages, totalRecords, currentPage } = res;

      const filtered = comparator(data);
      setRowData(filtered);
      setTotalPages(Number(totalPages));
      setCurrentPage(Number(currentPage));
      setTotalRecords(Number(totalRecords));
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      isFetchingRef.current = false;
    }
  };

  // ✅ Initial load
  const fetchInitialData = async (params) => {
    try {
      const latestGroup = allGroups?.[0]?.groupName;
      if (!latestGroup) return;

      setSelectedGroup(latestGroup);
      await fetchData(latestGroup, 1);

      // Initialize filter only once
      if (!columnDefsInitializedRef.current) {
        const colDef = params.api.getColumnDef('groupName');
        if (colDef && colDef.filterParams) {
          colDef.filterParams.values = [
            ...new Set(allGroups?.map((i) => i?.groupName)),
          ];
          params.api.setColumnDefs(params.api.getColumnDefs());
        }
        columnDefsInitializedRef.current = true;
      }
    } catch (err) {
      console.error(err);
    }
  };

  const onGridReady = async (params) => {
    gridApiRef.current = params.api;
    await fetchInitialData(params);
  };

  const onFilterChanged = async (params) => {
    try {
      const filterModel = params.api.getFilterModel();
      const selected =
        filterModel?.groupName?.state || allGroups?.[0]?.groupName;
      if (!selected) return;

      setSelectedGroup(selected);
      setCurrentPage(1);
      await fetchData(selected, 1);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (selectedGroup && !isFetchingRef.current) {
      fetchData(selectedGroup, currentPage);
    }
  }, [currentPage]);

  const paginationBtnStyle = (disabled) => ({
    width: 32,
    height: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid #E5E7EB',
    borderRadius: 4,
    background: '#fff',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.4 : 1,
    fontSize: 13,
    color: '#374151',
  });

  return (
    <div
      style={{
        backgroundColor: '#fff',
        borderRadius: 16,
        border: '1px solid rgba(0,0,0,0.07)',
        boxShadow: '0 1px 6px rgba(0,0,0,0.05)',
        overflow: 'hidden',
      }}
    >
      <div className='ag-theme-alpine' style={{ width: '100%' }}>
        <AgGridReact
          onGridReady={onGridReady}
          onFilterChanged={onFilterChanged}
          columnDefs={columnDefs()}
          defaultColDef={defaultColDef}
          rowData={rowData}
          domLayout="autoHeight"
          rowHeight={50}
          animateRows
          pagination={false}
        />
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 16px',
          borderTop: '1px solid #F3F4F6',
          backgroundColor: '#fff',
        }}
      >
        <span style={{ fontSize: 12, color: '#6B7280' }}>
          {totalRecords === 0 ? 0 : (currentPage - 1) * pageSize + 1}
          {' – '}
          {Math.min(currentPage * pageSize, totalRecords)}
          {' of '}
          <span style={{ fontWeight: 600, color: '#111827' }}>{totalRecords}</span>
        </span>

        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <button
            style={paginationBtnStyle(currentPage <= 1)}
            onClick={() => currentPage > 1 && setCurrentPage(1)}
          >
            ⏮
          </button>
          <button
            style={paginationBtnStyle(currentPage <= 1)}
            onClick={() => currentPage > 1 && setCurrentPage((p) => p - 1)}
          >
            ◀
          </button>
          <span style={{ fontSize: 12, color: '#374151', padding: '0 8px' }}>
            Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
          </span>
          <button
            style={paginationBtnStyle(currentPage >= totalPages)}
            onClick={() => currentPage < totalPages && setCurrentPage((p) => p + 1)}
          >
            ▶
          </button>
          <button
            style={paginationBtnStyle(currentPage >= totalPages)}
            onClick={() => currentPage < totalPages && setCurrentPage(totalPages)}
          >
            ⏭
          </button>
        </div>
      </div>
    </div>
  );
}

export default ManageClassroomCssTable;
